import React, { type JSX } from 'react';
import { Mutation } from '@apollo/client/react/components';
import {
  IData as IObjectUpdateData,
  ObjectUpdateMutationFnType,
  IVariables as IObjectUpdateVariables,
  IValues as IObjectUpdateMutationValues,
  ObjectUpdateMutationReturnType,
  createUpdateGroupMutation,
  getUpdateGroupMutationCollectionVariables,
  getUpdateGroupMutationCollectionOptimisticResponse,
} from './ObjectUpdateMutationBase';
import { IAttributeField } from '../../../../../Components/Attributes/Form/Components/ConnectedAttributeGroup/ConnectedAttributeGroup';
import { groupBy, any } from 'ramda';

interface IProps {
  database: string;
  attributeFields: IAttributeField[];
  children: (
    mutation: (
      values: IObjectUpdateMutationValues
    ) => ObjectUpdateMutationReturnType
  ) => JSX.Element;
}

type AttributeFieldWithoutCollectionInfoType = Pick<
  IAttributeField,
  'attribute' | 'value' | 'attributePath'
>;

export interface ICollection {
  name: string;
  id?: string;
  _version?: number;
  attributes: AttributeFieldWithoutCollectionInfoType[];
}

const getCollectionsFromAttributeFields = (
  attributeFields: IAttributeField[]
): ICollection[] => {
  const groupedAfterCollectionName = groupBy(
    (attrField: IAttributeField) => attrField.collection.CollectionName,
    attributeFields
  );

  const collections: ICollection[] = [];

  for (let key in groupedAfterCollectionName) {
    if (
      Object.prototype.hasOwnProperty.call(!groupedAfterCollectionName, key)
    ) {
      continue;
    }
    const attributeFields = groupedAfterCollectionName[key];

    if (
      any(
        attributeField =>
          attributeField._version !== attributeFields[0]._version ||
          attributeField.id !== attributeFields[0].id,
        attributeFields
      )
    ) {
      throw new Error(
        `Not all attribute under the collection: ${key} have the same id or version`
      );
    }

    collections.push({
      name: key,
      _version: attributeFields[0]._version,
      id: attributeFields[0].id,
      attributes: attributeFields.map(attrField => ({
        attribute: attrField.attribute,
        value: attrField.value,
        attributePath: attrField.attributePath,
      })),
    });
  }

  return collections;
};

class ObjectUpdateMutation extends React.Component<IProps> {
  mutate =
    (collections: ICollection[], mutationFn: ObjectUpdateMutationFnType) =>
    (values: IObjectUpdateMutationValues): ObjectUpdateMutationReturnType => {
      const collectionVariables = getUpdateGroupMutationCollectionVariables(
        collections,
        values
      );
      const variables: IObjectUpdateVariables = {
        databaseName: this.props.database,
        ...collectionVariables,
      };

      return mutationFn({
        variables: variables,
        optimisticResponse: {
          db: {
            ...getUpdateGroupMutationCollectionOptimisticResponse(
              collections,
              values
            ),
            __typename: 'TEDAvenyDBMutation',
          },
        },
      });
    };

  render() {
    const collections = getCollectionsFromAttributeFields(
      this.props.attributeFields
    );

    const mutation = createUpdateGroupMutation(collections);

    return (
      <Mutation<IObjectUpdateData, IObjectUpdateVariables> mutation={mutation}>
        {objectUpdateMutation => {
          return this.props.children(
            this.mutate(collections, objectUpdateMutation)
          );
        }}
      </Mutation>
    );
  }
}

export default ObjectUpdateMutation;
